import type { DomainGuide } from '@/components/guide/guide-types';

import th from './th.json';
import en from './en.json';
import zh from './zh.json';
import vi from './vi.json';
import km from './km.json';
import lo from './lo.json';
import my from './my.json';

/**
 * Help catalog for the `users` domain — the staff list at `/users`.
 *
 * The load-bearing distinction is LEVEL vs ROLE. The role is the permission
 * list; the level is a plain number on the staff row that svc-auth bakes into
 * the access token, and `rbacGuard` returns before consulting any permission
 * when it is 6 or >= 90 (packages/shared/src/middleware/rbac-guard.ts:31-32).
 * So a level-6 account with an EMPTY role holds the whole platform. The form
 * offers level 6 in the same dropdown as level 1, which is why every caution
 * here names the two separately.
 *
 * Second-order but just as easy to get wrong: the level rides in the token
 * (apps/auth/src/routes/auth.ts:410-423) and is re-read only when the token
 * rotates, while a role's PERMISSIONS are resolved per request against a 60s
 * Redis cache (apps/api-gateway/src/rbac-resolver.ts:32). The two therefore
 * take effect on completely different timescales.
 */
export const usersGuide: DomainGuide = {
  domain: 'users',
  pages: [
    {
      id: 'users.list',
      route: '/users',
      // Gateway: `admins` reads = admin:read, ALL writes (create, edit,
      // suspend, reset-password, delete) = admin:update
      // (api-gateway/src/upstreams.ts:373-374).
      permission: 'admin:read',
      status: 'live',
      fields: [
        { key: 'username', kind: 'text' },
        { key: 'email', kind: 'text' },
        { key: 'role', kind: 'text' },
        { key: 'level', kind: 'text' },
        { key: 'status', kind: 'status' },
        { key: 'lastLoginAt', kind: 'date' },
      ],
      cautionCount: 8,
      warningCount: 4,
    },
  ],
  copy: { th, en, zh, vi, km, lo, my },
};
